import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  useMediaQuery,
} from "@mui/material";

import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { getParties, getPartyLegalFiles } from "../../services/PartyService";
import CardHeader from "../General/CardHeader";
import DataGridCustom from "../General/DatagridCustom";
import AppDialog from "../General/Dialog/AppDialog";
import GlassCard from "../General/GlassCard";
import LoadingState from "../General/LoadingState";

import { useTheme } from "@mui/material/styles";
import { useToast } from "../../context/ToastContext";

export default function PartySearch() {
  const { showToast } = useToast();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [openModal, setOpenModal] = useState(false);
  const [selectedParty, setSelectedParty] = useState(null);
  const [legalFilesLoading, setLegalFilesLoading] = useState(false);
  const [legalFilesError, setLegalFilesError] = useState("");

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [legalFilesRows, setLegalFilesRows] = useState([]);
  const [legalFilesTotal, setLegalFilesTotal] = useState(0);

  const [legalFilesPagination, setLegalFilesPagination] = useState({
    page: 0,
    pageSize: 50,
  });

  const [legalFilesSearch, setLegalFilesSearch] = useState("");

  const fetchParties = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;

      const resp = await getParties({
        page: page + 1,
        perPage: pageSize,
        search: search.trim(),
      });

      if (resp.data?.status === "success") {
        setRows(resp.data.data || []);
        setRowCount(resp.data.total || 0);
      } else {
        setRows([]);
        setRowCount(0);
        showToast("No se pudo cargar el listado de partes.", "error")
      }
    } catch (e) {
      showToast("Ocurrió un error cargando las partes.", "error")
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, search, showToast]);

  const fetchLegalFiles = useCallback(async () => {
    if (!openModal || !selectedParty?.id) return;

    setLegalFilesLoading(true);
    setLegalFilesError("");

    try {
      const resp = await getPartyLegalFiles(selectedParty.id, {
        page: legalFilesPagination.page + 1,
        perPage: legalFilesPagination.pageSize,
        search: legalFilesSearch.trim(),
      });

      if (resp.data?.status === "success") {
        setLegalFilesRows(resp.data.data || []);
        setLegalFilesTotal(resp.data.total || 0);
      } else {
        setLegalFilesRows([]);
        setLegalFilesTotal(0);
        setLegalFilesError("No se pudieron cargar los expedientes asociados.");
      }
    } catch (e) {
      setLegalFilesRows([]);
      setLegalFilesTotal(0);
      setLegalFilesError("Ocurrió un error cargando los expedientes.");
      console.error(e);
    } finally {
      setLegalFilesLoading(false);
    }
  }, [
    openModal,
    selectedParty?.id,
    legalFilesPagination.page,
    legalFilesPagination.pageSize,
    legalFilesSearch,
  ]);

  useEffect(() => {
    fetchParties();
  }, [fetchParties]);

  useEffect(() => {
    fetchLegalFiles();
  }, [fetchLegalFiles]);

  const handleSearch = useCallback(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const openLegalFilesModal = useCallback(async (party) => {
    setSelectedParty(party);
    setOpenModal(true);

    setLegalFilesRows([]);
    setLegalFilesTotal(0);
    setLegalFilesSearch("");
    setLegalFilesPagination({ page: 0, pageSize: 50 });
  }, []);

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedParty(null);
    setLegalFilesRows([]);
    setLegalFilesTotal(0);
    setLegalFilesError("");
    setLegalFilesSearch("");
    setLegalFilesPagination({ page: 0, pageSize: 50 });
  };

  const partyColumns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      { field: "role", headerName: "Rol", flex: 1 },
      { field: "name", headerName: "Nombre", flex: 1 },
      { field: "lastname", headerName: "Apellido", flex: 1 },
      { field: "document", headerName: "Documento", flex: 1 },
      { field: "legal_files_count", headerName: "Expedientes" },
      {
        field: "actions",
        headerName: "Acciones",
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton onClick={() => openLegalFilesModal(params.row)}>
            <VisibilityIcon />
          </IconButton>
        ),
      },
    ],
    [openLegalFilesModal],
  );

  const legalFilesColumns = useMemo(
    () => [
      { field: "code", headerName: "Código", flex: 1, minWidth: 140 },
      { field: "name", headerName: "Nombre", flex: 1, minWidth: 220 },
      { field: "status", headerName: "Estado", flex: 1, minWidth: 130 },
      { field: "location", headerName: "Ubicación", flex: 1, minWidth: 150 },
      {
        field: "dependencies",
        headerName: "Dependencia",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "detail",
        headerName: "Detalle",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="Abrir detalle en otra pestaña">
            <IconButton
              component="a"
              href={`/legal/file/${params.row.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 11, lg: 11 }}>

        <GlassCard>
          <CardHeader
            icon={<PeopleAltIcon />}
            title="Partes Legales"
            description="Buscá partes (nombre, apellido, documento, rol) y consultá sus expedientes asociados."
          />

          <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 2 }}>
            <TextField
              label="Buscar"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />

            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SearchIcon />
                )
              }
              onClick={handleSearch}
              disabled={loading}
              sx={{ minWidth: 140 }}
            >
              Buscar
            </Button>
          </Box>

          <DataGridCustom
            rows={rows}
            columns={partyColumns}
            success={false}
            paginationModel={paginationModel}
            onPaginationChange={setPaginationModel}
            total={rowCount}
            loading={loading}
            isServerPagination={true}
          />
        </GlassCard>
      </Grid>

      <AppDialog
        open={openModal}
        onClose={handleCloseModal}
        title="Expedientes asociados"
        subtitle={
          selectedParty
            ? `${`${selectedParty.lastname ?? ""} ${selectedParty.name ?? ""}`.trim()}`
            : ""
        }
        maxWidth="lg"
        fullScreenMobile
        fullScreenBreakpoint="md"
        stickyHeader
        contentSx={{ p: { xs: 1.2, sm: 2 }, background: "transparent" }}
        paperSx={{ borderRadius: isMobile ? 0 : 4, overflow: "hidden" }}
        // actions={
        //   <Button
        //     onClick={handleCloseModal}
        //     variant="contained"
        //     sx={{ borderRadius: 2, fontWeight: 900 }}
        //   >
        //     Cerrar
        //   </Button>
        // }
      >
        <Box
          sx={{
            mb: 1.2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
            flexWrap: "wrap",
          }}
        >
          {selectedParty?.document ? (
            <Chip
              size="small"
              label={selectedParty.document}
              sx={{
                height: 22,
                borderRadius: 999,
                fontWeight: 900,
                bgcolor: "rgba(0,0,0,0.06)",
                "& .MuiChip-label": { px: 1, fontSize: 12 },
              }}
            />
          ) : null}

          <Chip
            size="small"
            label={`${legalFilesTotal} expedientes`}
            sx={{
              height: 22,
              borderRadius: 999,
              fontWeight: 900,
              bgcolor: "rgba(25,118,210,0.10)",
              color: "primary.main",
              "& .MuiChip-label": { px: 1, fontSize: 12 },
            }}
          />
        </Box>

        {legalFilesError && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {legalFilesError}
          </Alert>
        )}

        {legalFilesLoading ? (
          <LoadingState text="Cargando expedientes…" variant="inline" />
        ) : (
          <Box
            sx={{
              borderRadius: 3,
              border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
              background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.65)",
              overflow: "hidden",
              "& .MuiPaper-root": { boxShadow: "none" },
            }}
          >
            <DataGridCustom
              rows={legalFilesRows}
              columns={legalFilesColumns}
              loading={legalFilesLoading}
              getRowId={(r) => r.id}
              paginationModel={legalFilesPagination}
              onPaginationChange={setLegalFilesPagination}
              total={legalFilesTotal}
              isServerPagination={true}
            />
          </Box>
        )}
      </AppDialog>
    </Grid>
  );
}
